import {camelCase, map, isObject, chain, isArray} from 'lodash';

export const objToCamelCase = <T>(obj: any): T => {
  return chain(obj)
    .mapKeys((value, key) => camelCase(key))
    .mapValues((value) => {
      if (isArray(value)) {
        if (value.every(item => typeof item === 'string')) // Fix: array contiene solo strings (lo devolvemos tal cual).
          return value;
        return objsToCamelCase(value);
      } else if (isObject(value))
        return objToCamelCase(value);
      else
        return value;
    })
    .value();
};

export const objsToCamelCase = <T>(objs: any[]): T[] => {
  if (isArray(objs))
    return map(objs, (obj) => objToCamelCase(obj));
  else if (isObject(objs))
    return objToCamelCase(objs);
  else
    return objs;
};

/**
 * Validates language fields in a given array of language objects to check for mandatory field completeness.
 *
 * @template T
 * @param {any[]} languages - An array of language objects to be verified.
 * @param {string[]} mandatoryFields - An array of field names that are required to be validated in each language object.
 * @param {string[]} allLanguageFields - An array of all possible language field names to check
 *                                       if at least one is populated in each language object.
 * @returns {T[]} An array of faulty language identifiers where mandatory fields are not fulfilled
 *                while at least one other language-specific field is populated.
 */
export const verifyLanguageFields = <T>(languages: any[], mandatoryFields: string[], allLanguageFields: string[]): T[] => {
  const faultLanguages: any = [];
  for (let i = 1; i < languages.length; i++) {
      let someFieldFilled = false;
      const currentLang = languages[i];
      for (const field of allLanguageFields) {
        if (currentLang.hasOwnProperty(field) && currentLang[field] !== null && currentLang[field] !== undefined && currentLang[field] !== '') {
            someFieldFilled = true;
            break;
        }
      }
      if(someFieldFilled){
        for (const field of mandatoryFields) {
          if (!currentLang.hasOwnProperty(field) || currentLang[field] === null || currentLang[field] === undefined || currentLang[field] === '') {
            
            faultLanguages.push(currentLang.idioma);
            break;
          }
        }
      }
  }

  return faultLanguages;
};

export interface FaultyLanguage {
  lang: string; // Idioma
  field: string; // Campo obligatorio faltante
}

/**
 * Verifies the language fields within a list of languages to ensure mandatory fields are present when any field is filled.
 * Returns details of faulty languages and fields (as verifyLanguageFields but naming fields).
 *
 * This function iterates through a list of language objects starting from the second element and checks if any field
 * specified in `allLanguageFields` is filled within the current language object. If so, it validates that all the
 * mandatory fields listed in `mandatoryFields` are present and properly filled. If any mandatory field is missing
 * or invalid, it logs the language and the missing field for errors.
 *
 * The function returns a concatenated string with details of languages and fields that failed validation.
 *
 * @param {any[]} languages - Array of language objects to validate. Each object should represent a language.
 * @param {string[]} mandatoryFields - Array of field names that must be present and contain valid values if any field is filled.
 * @param {string[]} allLanguageFields - Array of all language field names to check for any filled field within an object.
 * @returns {string} A string detailing the languages and respective fields that failed validation,
 *                   separated by " | ". An empty string is returned if no errors.
 */
export const verifyLanguageFieldsWithDetails = (
  languages: any[],
  mandatoryFields: string[],
  allLanguageFields: string[]
): string => {
  const faultLanguages: FaultyLanguage[] = [];
  for (let i = 1; i < languages.length; i++) {
      let someFieldFilled = false;
      const currentLang = languages[i];
      for (const field of allLanguageFields) {
        if (currentLang.hasOwnProperty(field)
          && currentLang[field] !== null
          && currentLang[field] !== undefined
          && currentLang[field] !== ''
        ) {
            someFieldFilled = true;
            break;
        }
      }

      if (someFieldFilled) {
        for (const field of mandatoryFields) {
          if (!currentLang.hasOwnProperty(field)
            || currentLang[field] === null
            || currentLang[field] === undefined
            || currentLang[field] === ''
          ) {
            faultLanguages.push({
              'lang': currentLang.idioma,
              'field': field
            });
            break;
          }
        }
      }
  }

  const errorDetails = faultLanguages
    .map(obj => `${obj.lang}: ${obj.field}`)
    .join(' | ')
  ;

  return errorDetails;
};

export const getLanguagesFilled = <T>(languages: any[], mandatoryFields: string[]): T[] => {
  const languagesFilled: any = [];
  languages.forEach((language: any) => {
    if (language[mandatoryFields[0]] !== null && language[mandatoryFields[0]] !== undefined && 
      language[mandatoryFields[0]] !== '') {
      languagesFilled.push(language);
    } else {
      if(language['id']){
        language.delete = 1;
        languagesFilled.push(language);
      }
    }
  });

  return languagesFilled;
};
